import React, { useState, useEffect, useRef } from 'react';

export default function CustomSelect({ options, value, onChange, placeholder = 'Select...', testId }) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectedLabel = options.find(o => o.value === value)?.label || placeholder;

  return (
    <div
      ref={wrapperRef}
      style={{ position: 'relative', width: '100%', display: 'block' }}
      data-testid={testId}
    >
      <div
        className="input-field"
        onClick={() => setIsOpen(!isOpen)}
        role="combobox"
        aria-expanded={isOpen}
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen(!isOpen);
          }
          if (e.key === 'Escape') setIsOpen(false);
        }}
        style={{
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: value ? 'var(--tx)' : 'var(--tx3)',
          borderColor: isOpen ? 'var(--teal)' : undefined,
          boxShadow: isOpen ? '0 0 8px rgba(45,212,191,0.15)' : undefined,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {selectedLabel}
        </span>
        <span style={{
          color: 'var(--tx3)',
          fontSize: '0.7rem',
          transition: 'transform 0.2s',
          transform: isOpen ? 'rotate(180deg)' : 'none',
          flexShrink: 0,
          marginLeft: '8px',
        }}>&#9660;</span>
      </div>

      {isOpen && (
        <div
          data-testid={`${testId}-dropdown`}
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            bottom: 'auto',
            left: 0,
            right: 0,
            maxHeight: '280px',
            overflowY: 'auto',
            background: 'var(--bg-alt, var(--bg))',
            border: '1px solid var(--brd)',
            borderRadius: '10px',
            zIndex: 9999,
            boxShadow: '0 12px 32px rgba(0,0,0,0.3)',
            pointerEvents: 'auto',
          }}
        >
          {options.map((opt, idx) => (
            <div
              key={opt.value}
              role="option"
              onClick={() => { onChange(opt.value); setIsOpen(false); }}
              data-testid={`${testId}-option-${idx}`}
              data-value={opt.value}
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                color: opt.value === value ? 'var(--teal)' : 'var(--tx)',
                background: opt.value === value ? 'rgba(45,212,191,0.08)' : 'transparent',
                fontSize: 'var(--fs-card, 1rem)',
                transition: 'background 0.15s',
                borderBottom: idx < options.length - 1 ? '1px solid var(--brd)' : 'none',
                pointerEvents: 'auto',
              }}
              onMouseEnter={e => {
                if (opt.value !== value) e.target.style.background = 'rgba(255,255,255,0.04)';
              }}
              onMouseLeave={e => {
                e.target.style.background = opt.value === value ? 'rgba(45,212,191,0.08)' : 'transparent';
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
